import { Camera, Image as ImageIcon, Sparkle } from '@phosphor-icons/react';
import {
  LEISURE_TEXT,
  type Assignees,
  type Idea,
  type IdeaLength,
  type IdeaPlace,
  type MagicVoucher,
  type Spot,
  type Voucher,
  type Wish,
} from '@shh/shared';
import { Busy, Button, ChoiceGroup, Icon, Text, TextField } from '@shh/ui';
import { useEffect, useEffectEvent, useRef, useState, type ChangeEvent } from 'react';
import { Link } from 'react-router';
import { useLens } from '../app/lens';
import { friendlyError, magicAdd, saveIdea, saveVoucher, saveWish } from '../data/api';
import { useFamily, type WithId } from '../data/family';
import { Actions, More, TitleField, WhoField, useSave } from './forms';
import { shrink } from './MagicAdd';
import { SpotSearch } from './Spot';
import { locale } from '../lib/locale';
import { funText } from '../i18n/fun';

const PLACES: { value: IdeaPlace; label: string; description: string }[] = (
  ['out', 'in', 'any'] as const
).map((value) => ({ value, ...funText.places[value] }));

/** A trip idea – everybody may add one; whoever adds it gets the first heart. */
export function IdeaForm({ idea, onDone }: { idea?: WithId<Idea>; onDone: () => void }) {
  const f = useFamily();
  const lens = useLens();
  const [title, setTitle] = useState(idea?.title ?? '');
  const [place, setPlace] = useState<IdeaPlace>(idea?.place ?? 'any');
  const [length, setLength] = useState<IdeaLength | 'none'>(idea?.length ?? 'none');
  const [cost, setCost] = useState(idea?.cost ?? '');
  const [note, setNote] = useState(idea?.note ?? '');
  const [spot, setSpot] = useState<Spot | undefined>(idea?.spot);
  const { busy, error, save } = useSave(onDone);
  // on the wall the person chip says whose heart it is; the wall itself has no person
  const heart = f.meId ?? lens.member?.id;
  return (
    <form className="flex flex-col gap-gap-lg" onSubmit={(e) => e.preventDefault()}>
      <TitleField
        label={funText.whereWhat}
        placeholder={funText.ideaExample}
        value={title}
        onChange={setTitle}
        fallback="tree"
      />
      {/* type the name – the map finds the place, one tap takes over address, hours, website */}
      <SpotSearch
        query={title}
        spot={spot}
        onPick={({ spot: s, hit, free }) => {
          setSpot(s);
          setTitle(s.name);
          if (hit.place !== 'any') setPlace(hit.place);
          if (free && !cost.trim()) setCost(funText.free);
        }}
        onClear={() => setSpot(undefined)}
      />
      <ChoiceGroup label={funText.where} value={place} onChange={setPlace} choices={PLACES} />
      <ChoiceGroup
        label={funText.howLong}
        value={length}
        onChange={setLength}
        choices={[
          { value: 'none', label: funText.dontKnow },
          ...(Object.keys(LEISURE_TEXT.de.length) as IdeaLength[]).map((k) => ({
            value: k,
            label: LEISURE_TEXT[locale].length[k],
          })),
        ]}
      />
      <More label={funText.moreIdea}>
        <TextField
          label={funText.costs}
          placeholder={funText.costsExample}
          value={cost}
          onChange={setCost}
          maxLength={30}
        />
        <TextField
          label={funText.note}
          placeholder={funText.noteExample}
          value={note}
          onChange={setNote}
          maxLength={280}
        />
      </More>
      <Actions
        onCancel={onDone}
        saveLabel={idea ? funText.save : funText.saveIdea}
        disabled={!title.trim()}
        busy={busy}
        error={error}
        onSave={() =>
          save(() =>
            saveIdea(
              f.householdId,
              {
                id: idea?.id,
                title: title.trim(),
                place,
                length: length === 'none' ? undefined : length,
                cost: cost.trim() || undefined,
                note: note.trim() || undefined,
                spot,
              },
              { createdBy: f.meId, heart },
            ),
          )
        }
      />
    </form>
  );
}

/** The KI reads a voucher photo: what it found (null = no voucher on it); throws on trouble. */
export async function readVoucherPhoto(file: File): Promise<MagicVoucher | null> {
  const img = await shrink(file);
  const { items } = await magicAdd({
    text: funText.readVoucher,
    purpose: 'voucher',
    image: { mimeType: img.mimeType, data: img.data },
  });
  return items.find((i): i is MagicVoucher => i.kind === 'voucher') ?? null;
}

/** What the KI read from a voucher photo – or why it could not. */
type Reading =
  { status: 'busy' } | { status: 'done'; found: boolean } | { status: 'error'; message: string };

/**
 * A voucher from the drawer. With the KI-Helfer a photo fills the form (what, value, valid until,
 * code, from whom) – the family checks it and adds where it lies. The photo itself is not kept.
 */
export function VoucherForm({
  voucher,
  photo,
  onDone,
}: {
  voucher?: WithId<Voucher>;
  /** a photo taken on the Gutscheine page, already being read */
  photo?: Promise<MagicVoucher | null>;
  onDone: () => void;
}) {
  const f = useFamily();
  const [title, setTitle] = useState(voucher?.title ?? '');
  const [value, setValue] = useState(voucher?.value ?? '');
  const [until, setUntil] = useState(voucher?.until ?? '');
  const [code, setCode] = useState(voucher?.code ?? '');
  const [place, setPlace] = useState(voucher?.place ?? '');
  const [from, setFrom] = useState(voucher?.from ?? '');
  const [forWhom, setForWhom] = useState<Assignees>(voucher?.forWhom ?? 'all');
  const [note, setNote] = useState(voucher?.note ?? '');
  // a photo from the page is being read from the start
  const [reading, setReading] = useState<Reading | undefined>(
    photo ? { status: 'busy' } : undefined,
  );
  const { busy, error, save } = useSave(onDone);
  // AI only for parents – the wall with the PIN (Mike, 24.10.: "KI rechtssicher")
  const mayAi = useLens().canParent;
  const ai = Boolean(f.household?.ai?.enabled) && mayAi;
  const camera = useRef<HTMLInputElement>(null);
  const files = useRef<HTMLInputElement>(null);

  const fill = (v: MagicVoucher) => {
    setTitle(v.title);
    if (v.value) setValue(v.value);
    if (v.until) setUntil(v.until);
    if (v.code) setCode(v.code);
    if (v.from) setFrom(v.from);
  };
  const show = (reading: Promise<MagicVoucher | null>) =>
    reading.then(
      (found) => {
        if (found) fill(found);
        setReading({ status: 'done', found: Boolean(found) });
      },
      (e: unknown) => setReading({ status: 'error', message: friendlyError(e) }),
    );
  const read = (file: File) => {
    setReading({ status: 'busy' });
    void show(readVoucherPhoto(file));
  };
  // the photo from the page's camera button: show what the KI read once it is there
  const showPhoto = useEffectEvent((p: Promise<MagicVoucher | null>) => void show(p));
  useEffect(() => {
    if (photo) showPhoto(photo);
  }, [photo]);
  const pick = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) read(file);
  };

  return (
    <form className="flex flex-col gap-gap-lg" onSubmit={(e) => e.preventDefault()}>
      {!voucher && ai && (
        <div className="flex flex-col gap-gap-sm rounded-card bg-surface-sunken p-inset-sm">
          <div className="flex flex-wrap gap-gap-sm">
            <Button
              variant="secondary"
              icon={Camera}
              onPress={() => camera.current?.click()}
              isDisabled={reading?.status === 'busy'}
            >
              {funText.takePhoto}
            </Button>
            <Button
              variant="quiet"
              icon={ImageIcon}
              onPress={() => files.current?.click()}
              isDisabled={reading?.status === 'busy'}
            >
              {funText.pickImage}
            </Button>
          </div>
          <input
            ref={camera}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={pick}
          />
          <input ref={files} type="file" accept="image/*" hidden onChange={pick} />
          {reading?.status === 'busy' ? (
            <Busy variant="ai" label={funText.reading} className="text-sm" />
          ) : (
            <Text size="sm" tone="muted" role="status">
              {reading?.status === 'done'
                ? reading.found
                  ? funText.readDone
                  : funText.readNothing
                : reading?.status === 'error'
                  ? reading.message
                  : funText.readHint}
            </Text>
          )}
        </div>
      )}
      {!voucher && !ai && (
        <Text size="sm" tone="muted" className="flex items-start gap-gap-xs">
          <Icon icon={Sparkle} size="sm" className="mt-0.5 shrink-0" />
          <span>
            {funText.aiHint}{' '}
            <Link to="/einstellungen" className="underline">
              {funText.settings}
            </Link>
          </span>
        </Text>
      )}
      <TitleField
        label={funText.whatWhere}
        placeholder={funText.voucherExample}
        value={title}
        onChange={setTitle}
        fallback="gift"
      />
      <div className="grid grid-cols-1 gap-gap-md sm:grid-cols-2">
        <TextField
          label={funText.value}
          placeholder={funText.valueExample}
          value={value}
          onChange={setValue}
          maxLength={40}
        />
        <TextField
          label={funText.validUntil}
          description={funText.validHint}
          type="date"
          value={until}
          onChange={setUntil}
        />
      </div>
      <TextField
        label={funText.liesInLabel}
        placeholder={funText.placeExample}
        description={funText.placeHint}
        value={place}
        onChange={setPlace}
        maxLength={60}
      />
      <WhoField label={funText.forWhom} value={forWhom} onChange={setForWhom} people={f.members} />
      <More label={funText.moreVoucher}>
        <TextField label={funText.code} value={code} onChange={setCode} maxLength={60} />
        <TextField
          label={funText.fromWhom}
          placeholder={funText.fromExample}
          value={from}
          onChange={setFrom}
          maxLength={40}
        />
        <TextField label={funText.note} value={note} onChange={setNote} maxLength={280} />
      </More>
      <Actions
        onCancel={onDone}
        saveLabel={voucher ? funText.save : funText.addVoucher}
        disabled={!title.trim() || reading?.status === 'busy'}
        busy={busy}
        error={error}
        onSave={() =>
          save(() =>
            saveVoucher(
              f.householdId,
              {
                id: voucher?.id,
                title: title.trim(),
                value: value.trim() || undefined,
                until: until || undefined,
                code: code.trim() || undefined,
                place: place.trim() || undefined,
                from: from.trim() || undefined,
                forWhom,
                note: note.trim() || undefined,
              },
              f.meId,
            ),
          )
        }
      />
    </form>
  );
}

const LOVE = funText.love.map((l) => ({ ...l }));

/** People type "fahrrad.de/…" – make it a link the browser can open. */
const asLink = (v: string) => {
  const t = v.trim();
  if (!t) return undefined;
  const url = /^https?:\/\//i.test(t) ? t : `https://${t}`;
  try {
    return new URL(url).href;
  } catch {
    return undefined;
  }
};

/** A wish on somebody's list ('family' = the shared family wishes). */
export function WishForm({
  owner,
  wish,
  onDone,
}: {
  owner: string;
  wish?: WithId<Wish>;
  onDone: () => void;
}) {
  const f = useFamily();
  const lens = useLens();
  const [title, setTitle] = useState(wish?.title ?? '');
  const [love, setLove] = useState(String(wish?.love ?? 1));
  const [occasion, setOccasion] = useState<Wish['occasion']>(wish?.occasion ?? 'any');
  const [price, setPrice] = useState(wish?.price ?? '');
  const [link, setLink] = useState(wish?.link ?? '');
  const { busy, error, save } = useSave(onDone);
  const badLink = Boolean(link.trim()) && !asLink(link);
  return (
    <form className="flex flex-col gap-gap-lg" onSubmit={(e) => e.preventDefault()}>
      <TitleField
        label={owner === 'family' ? funText.familyWishAsk : funText.wishAsk}
        placeholder={owner === 'family' ? funText.familyWishExample : funText.wishExample}
        value={title}
        onChange={setTitle}
        fallback="gift"
      />
      <ChoiceGroup label={funText.howMuch} value={love} onChange={setLove} choices={LOVE} />
      <ChoiceGroup
        label={funText.forWhatAsk}
        value={occasion}
        onChange={setOccasion}
        choices={(Object.keys(LEISURE_TEXT.de.occasion) as Wish['occasion'][]).map((k) => ({
          value: k,
          label: LEISURE_TEXT[locale].occasion[k],
        }))}
      />
      <More label={funText.morePrice}>
        <TextField
          label={funText.price}
          placeholder={funText.priceExample}
          value={price}
          onChange={setPrice}
          maxLength={30}
        />
        <TextField
          label={funText.link}
          placeholder={funText.linkExample}
          type="url"
          value={link}
          onChange={setLink}
          isInvalid={badLink}
          errorMessage={funText.badLink}
        />
      </More>
      <Actions
        onCancel={onDone}
        saveLabel={wish ? funText.save : funText.toWishList}
        disabled={!title.trim() || badLink}
        busy={busy}
        error={error}
        onSave={() =>
          save(() =>
            saveWish(
              f.householdId,
              {
                id: wish?.id,
                owner,
                title: title.trim(),
                love: Number(love),
                occasion,
                price: price.trim() || undefined,
                link: asLink(link),
              },
              f.meId ?? (lens.member?.id || undefined),
            ),
          )
        }
      />
    </form>
  );
}
